import type { ElevationProfile, ElevationProfileSample } from '@freedom/elevation';
import type { Coordinates } from '@freedom/shared';

export const TERRAIN_PROFILE_CACHE_VERSION = 1 as const;
export const TERRAIN_PROFILE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1_000;
export const TERRAIN_PROFILE_MEMORY_LIMIT = 8;
export const TERRAIN_PROFILE_PERSISTENT_LIMIT = 24;
export const TERRAIN_PROFILE_PERSISTENT_BYTES = 12 * 1024 * 1024;

const TERRAIN_PROFILE_KEY_PREFIX = `freedom-isp-design:terrain-profile:v${TERRAIN_PROFILE_CACHE_VERSION}:`;
const COORDINATE_SCALE = 10_000_000;
const MAX_PROFILE_SAMPLES = 4_000;
const MAX_PROFILE_WARNINGS = 100;
const MAX_TEXT_LENGTH = 1_000;

export interface TerrainProfileCacheIdentity {
  start: Coordinates;
  end: Coordinates;
  sourcePolicy: string;
  optionsPolicy: string;
}

export interface TerrainProfileCacheRecord {
  version: typeof TERRAIN_PROFILE_CACHE_VERSION;
  key: string;
  createdAt: number;
  expiresAt: number;
  lastAccessedAt: number;
  approximateBytes: number;
  profile: ElevationProfile;
}

export interface AsyncTerrainProfileRecordStore {
  get(key: string): Promise<unknown | null>;
  set(record: TerrainProfileCacheRecord): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  list(): Promise<unknown[]>;
}

export interface TerrainProfileCacheOptions {
  memoryEntries?: number;
  persistentEntries?: number;
  maxPersistentBytes?: number;
  ttlMs?: number;
  now?: () => number;
}

function policyNamespace(value: string, label: string): string {
  const normalized = value.trim();
  if (normalized.length === 0 || normalized.length > 200) throw new RangeError(`${label} must contain between 1 and 200 characters`);
  return encodeURIComponent(normalized);
}

function coordinateToken(coordinates: Coordinates): string {
  if (!Number.isFinite(coordinates.lat) || !Number.isFinite(coordinates.lon) || Math.abs(coordinates.lat) > 90 || Math.abs(coordinates.lon) > 180) {
    throw new RangeError('Terrain profile coordinates are outside the valid range');
  }
  const latitude = Math.round(coordinates.lat * COORDINATE_SCALE);
  const longitude = Math.round(coordinates.lon * COORDINATE_SCALE);
  return `${Object.is(latitude, -0) ? 0 : latitude}:${Object.is(longitude, -0) ? 0 : longitude}`;
}

export function terrainProfileCacheKey(identity: TerrainProfileCacheIdentity): string {
  const source = policyNamespace(identity.sourcePolicy, 'sourcePolicy');
  const options = policyNamespace(identity.optionsPolicy, 'optionsPolicy');
  return `${TERRAIN_PROFILE_KEY_PREFIX}${source}:${options}:${coordinateToken(identity.start)}>${coordinateToken(identity.end)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function boundedText(value: unknown, allowEmpty = false): value is string {
  return typeof value === 'string' && value.length <= MAX_TEXT_LENGTH && (allowEmpty || value.length > 0);
}

function validSample(value: unknown): value is ElevationProfileSample {
  if (!isRecord(value)) return false;
  const sourceTypes = new Set(['DSM', 'DTM', 'LIDAR', 'MANUAL']);
  return typeof value.lat === 'number' && Number.isFinite(value.lat) && Math.abs(value.lat) <= 90
    && typeof value.lon === 'number' && Number.isFinite(value.lon) && Math.abs(value.lon) <= 180
    && typeof value.distanceM === 'number' && Number.isFinite(value.distanceM) && value.distanceM >= 0
    && typeof value.elevationM === 'number' && Number.isFinite(value.elevationM) && value.elevationM >= -500 && value.elevationM <= 9_000
    && typeof value.resolutionM === 'number' && Number.isFinite(value.resolutionM) && value.resolutionM > 0 && value.resolutionM <= 100_000
    && boundedText(value.source)
    && typeof value.sourceType === 'string' && sourceTypes.has(value.sourceType)
    && typeof value.confidence === 'number' && Number.isFinite(value.confidence) && value.confidence >= 0 && value.confidence <= 1;
}

export function isCacheableElevationProfile(value: unknown): value is ElevationProfile {
  if (!isRecord(value) || value.partial !== false || !Array.isArray(value.samples) || value.samples.length < 2 || value.samples.length > MAX_PROFILE_SAMPLES) return false;
  if (!Array.isArray(value.warnings) || value.warnings.length > MAX_PROFILE_WARNINGS || !value.warnings.every((warning) => boundedText(warning, true))) return false;
  if (!boundedText(value.source) || typeof value.resolutionM !== 'number' || !Number.isFinite(value.resolutionM) || value.resolutionM <= 0 || value.resolutionM > 100_000) return false;
  let previousDistance = -1;
  for (const sample of value.samples) {
    if (!validSample(sample) || sample.distanceM < previousDistance) return false;
    previousDistance = sample.distanceM;
  }
  return true;
}

export function estimateTerrainProfileBytes(profile: ElevationProfile): number {
  try { return JSON.stringify(profile).length * 2; } catch { return Number.POSITIVE_INFINITY; }
}

function positiveInteger(value: number | undefined, fallback: number): number {
  return value === undefined || !Number.isSafeInteger(value) || value < 1 ? fallback : value;
}

function positiveNumber(value: number | undefined, fallback: number): number {
  return value === undefined || !Number.isFinite(value) || value <= 0 ? fallback : value;
}

function validCacheKey(value: unknown): value is string {
  if (typeof value !== 'string' || !value.startsWith(TERRAIN_PROFILE_KEY_PREFIX) || value.length > 2_000) return false;
  return /^[^:]{1,600}:[^:]{1,600}:-?\d+:-?\d+>-?\d+:-?\d+$/u.test(value.slice(TERRAIN_PROFILE_KEY_PREFIX.length));
}

function validatedRecord(value: unknown, expectedKey: string | null, now: number, ttlMs: number, maximumBytes: number): TerrainProfileCacheRecord | null {
  if (!isRecord(value) || value.version !== TERRAIN_PROFILE_CACHE_VERSION || !validCacheKey(value.key) || (expectedKey !== null && value.key !== expectedKey)) return null;
  if (typeof value.createdAt !== 'number' || !Number.isFinite(value.createdAt) || typeof value.expiresAt !== 'number' || !Number.isFinite(value.expiresAt)) return null;
  if (typeof value.lastAccessedAt !== 'number' || !Number.isFinite(value.lastAccessedAt) || value.expiresAt <= now || value.expiresAt <= value.createdAt || value.expiresAt - value.createdAt > ttlMs) return null;
  if (value.lastAccessedAt < value.createdAt || value.lastAccessedAt > now + 60_000 || !isCacheableElevationProfile(value.profile)) return null;
  const approximateBytes = estimateTerrainProfileBytes(value.profile);
  if (!Number.isFinite(approximateBytes) || approximateBytes <= 0 || approximateBytes > maximumBytes) return null;
  return {
    version: TERRAIN_PROFILE_CACHE_VERSION,
    key: value.key,
    createdAt: value.createdAt,
    expiresAt: value.expiresAt,
    lastAccessedAt: value.lastAccessedAt,
    approximateBytes,
    profile: value.profile,
  };
}

export class TerrainProfileCache {
  private readonly memory = new Map<string, TerrainProfileCacheRecord>();
  private readonly memoryEntries: number;
  private readonly persistentEntries: number;
  private readonly maximumPersistentBytes: number;
  private readonly ttlMs: number;
  private readonly clock: () => number;
  private mutationEpoch = 0;
  private persistenceTail: Promise<void> = Promise.resolve();

  public constructor(private readonly store: AsyncTerrainProfileRecordStore | null = null, options: TerrainProfileCacheOptions = {}) {
    this.memoryEntries = positiveInteger(options.memoryEntries, TERRAIN_PROFILE_MEMORY_LIMIT);
    this.persistentEntries = positiveInteger(options.persistentEntries, TERRAIN_PROFILE_PERSISTENT_LIMIT);
    this.maximumPersistentBytes = positiveNumber(options.maxPersistentBytes, TERRAIN_PROFILE_PERSISTENT_BYTES);
    this.ttlMs = positiveNumber(options.ttlMs, TERRAIN_PROFILE_CACHE_TTL_MS);
    this.clock = options.now ?? Date.now;
  }

  public get memoryEntryCount(): number { return this.memory.size; }

  public async get(key: string): Promise<ElevationProfile | null> {
    if (!validCacheKey(key)) return null;
    const now = this.clock();
    const memoryRecord = this.memory.get(key);
    if (memoryRecord) {
      if (memoryRecord.expiresAt <= now) {
        this.mutationEpoch += 1;
        this.memory.delete(key);
        if (this.store) void this.enqueuePersistence(() => this.store?.delete(key) ?? Promise.resolve());
        return null;
      }
      const touched = { ...memoryRecord, lastAccessedAt: now };
      this.remember(touched);
      if (this.store) void this.enqueuePersistence(async () => {
        await this.store?.set(touched);
        await this.prunePersistent(now);
      });
      return touched.profile;
    }
    if (!this.store) return null;
    const epochAtReadStart = this.mutationEpoch;
    let raw: unknown | null;
    try { raw = await this.store.get(key); } catch { return null; }
    if (epochAtReadStart !== this.mutationEpoch) {
      const current = this.memory.get(key);
      if (!current || current.expiresAt <= this.clock()) return null;
      return current.profile;
    }
    if (raw === null) return null;
    const record = validatedRecord(raw, key, now, this.ttlMs, this.maximumPersistentBytes);
    if (!record) {
      void this.enqueuePersistence(() => this.store?.delete(key) ?? Promise.resolve());
      return null;
    }
    const touched = { ...record, lastAccessedAt: now };
    this.remember(touched);
    void this.enqueuePersistence(async () => {
      await this.store?.set(touched);
      await this.prunePersistent(now);
    });
    return touched.profile;
  }

  public async set(key: string, profile: ElevationProfile): Promise<boolean> {
    if (!validCacheKey(key) || !isCacheableElevationProfile(profile)) return false;
    const approximateBytes = estimateTerrainProfileBytes(profile);
    if (!Number.isFinite(approximateBytes) || approximateBytes <= 0 || approximateBytes > this.maximumPersistentBytes) return false;
    const now = this.clock();
    if (!Number.isFinite(now) || !Number.isFinite(now + this.ttlMs)) return false;
    const record: TerrainProfileCacheRecord = {
      version: TERRAIN_PROFILE_CACHE_VERSION,
      key,
      createdAt: now,
      expiresAt: now + this.ttlMs,
      lastAccessedAt: now,
      approximateBytes,
      profile,
    };
    this.mutationEpoch += 1;
    this.remember(record);
    if (this.store) await this.enqueuePersistence(async () => {
      await this.store?.set(record);
      await this.prunePersistent(now);
    });
    return true;
  }

  public async delete(key: string): Promise<void> {
    this.mutationEpoch += 1;
    this.memory.delete(key);
    if (this.store) await this.enqueuePersistence(() => this.store?.delete(key) ?? Promise.resolve());
  }

  public async clear(): Promise<void> {
    this.mutationEpoch += 1;
    this.memory.clear();
    if (this.store) await this.enqueuePersistence(() => this.store?.clear() ?? Promise.resolve());
  }

  /** Used for privacy revocation: unlike best-effort cache maintenance, deletion failures propagate. */
  public async clearPersistentStorage(): Promise<void> {
    this.mutationEpoch += 1;
    this.memory.clear();
    if (!this.store) return;
    const operation = this.persistenceTail.catch(() => undefined).then(() => this.store?.clear() ?? Promise.resolve());
    this.persistenceTail = operation.catch(() => undefined);
    await operation;
  }

  private remember(record: TerrainProfileCacheRecord): void {
    this.memory.delete(record.key);
    this.memory.set(record.key, record);
    while (this.memory.size > this.memoryEntries) {
      const oldest = this.memory.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      this.memory.delete(oldest);
    }
  }

  private enqueuePersistence(work: () => Promise<void>): Promise<void> {
    const operation = this.persistenceTail.catch(() => undefined).then(work);
    this.persistenceTail = operation.catch(() => undefined);
    return this.persistenceTail;
  }

  private async prunePersistent(now: number): Promise<void> {
    if (!this.store) return;
    const rawRecords = await this.store.list();
    const records: TerrainProfileCacheRecord[] = [];
    for (const raw of rawRecords) {
      const rawKey = isRecord(raw) && typeof raw.key === 'string' ? raw.key : null;
      const record = validatedRecord(raw, null, now, this.ttlMs, this.maximumPersistentBytes);
      if (record) records.push(record);
      else if (rawKey !== null) await this.store.delete(rawKey);
    }
    records.sort((left, right) => left.lastAccessedAt - right.lastAccessedAt || left.createdAt - right.createdAt || left.key.localeCompare(right.key));
    let totalBytes = records.reduce((total, record) => total + record.approximateBytes, 0);
    while (records.length > this.persistentEntries || totalBytes > this.maximumPersistentBytes) {
      const oldest = records.shift();
      if (!oldest) break;
      totalBytes -= oldest.approximateBytes;
      await this.store.delete(oldest.key);
    }
  }
}

function requestValue<Result>(request: IDBRequest<Result>): Promise<Result> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction was aborted'));
  });
}

export class IndexedDbTerrainProfileRecordStore implements AsyncTerrainProfileRecordStore {
  private databasePromise: Promise<IDBDatabase> | null = null;

  public constructor(
    private readonly factory: IDBFactory | null = typeof indexedDB === 'undefined' ? null : indexedDB,
    private readonly databaseName = 'freedom-isp-design-terrain',
    private readonly objectStoreName = 'profiles',
  ) {}

  public async get(key: string): Promise<unknown | null> {
    const database = await this.database();
    const transaction = database.transaction(this.objectStoreName, 'readonly');
    const [result] = await Promise.all([
      requestValue(transaction.objectStore(this.objectStoreName).get(key)),
      transactionComplete(transaction),
    ]);
    return result === undefined ? null : result;
  }

  public async set(record: TerrainProfileCacheRecord): Promise<void> {
    if (!validCacheKey(record.key) || !isCacheableElevationProfile(record.profile)) throw new Error('Terrain profile cache record is invalid');
    const database = await this.database();
    const transaction = database.transaction(this.objectStoreName, 'readwrite');
    transaction.objectStore(this.objectStoreName).put(record);
    await transactionComplete(transaction);
  }

  public async delete(key: string): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction(this.objectStoreName, 'readwrite');
    transaction.objectStore(this.objectStoreName).delete(key);
    await transactionComplete(transaction);
  }

  public async clear(): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction(this.objectStoreName, 'readwrite');
    transaction.objectStore(this.objectStoreName).clear();
    await transactionComplete(transaction);
  }

  public async list(): Promise<unknown[]> {
    const database = await this.database();
    const transaction = database.transaction(this.objectStoreName, 'readonly');
    const [result] = await Promise.all([
      requestValue(transaction.objectStore(this.objectStoreName).getAll()),
      transactionComplete(transaction),
    ]);
    return result as unknown[];
  }

  public close(): void {
    if (!this.databasePromise) return;
    void this.databasePromise.then((database) => database.close()).catch(() => undefined);
    this.databasePromise = null;
  }

  private database(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise;
    if (!this.factory) return Promise.reject(new Error('IndexedDB is unavailable'));
    this.databasePromise = new Promise((resolve, reject) => {
      const request = this.factory?.open(this.databaseName, TERRAIN_PROFILE_CACHE_VERSION);
      if (!request) { reject(new Error('IndexedDB is unavailable')); return; }
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(this.objectStoreName)) database.createObjectStore(this.objectStoreName, { keyPath: 'key' });
      };
      request.onsuccess = () => {
        const database = request.result;
        database.onversionchange = () => { database.close(); this.databasePromise = null; };
        resolve(database);
      };
      request.onerror = () => reject(request.error ?? new Error('IndexedDB could not be opened'));
      request.onblocked = () => reject(new Error('IndexedDB upgrade is blocked'));
    });
    return this.databasePromise.catch((error: unknown) => {
      this.databasePromise = null;
      throw error;
    });
  }
}

export function createBrowserTerrainProfileCache(options: TerrainProfileCacheOptions = {}): TerrainProfileCache {
  const store = typeof indexedDB === 'undefined' ? null : new IndexedDbTerrainProfileRecordStore(indexedDB);
  return new TerrainProfileCache(store, options);
}

/**
 * Keeps the RF workflow available in memory while treating persistent IndexedDB
 * storage as optional. Switching persistence off clears the store before it can
 * be used again, while new reads/writes immediately continue against memory.
 */
export class ConsentAwareTerrainProfileCache {
  private cache: TerrainProfileCache;
  private persistenceEnabled: boolean;
  private transitionTail: Promise<void> = Promise.resolve();

  public constructor(
    private readonly persistentCacheFactory: () => TerrainProfileCache,
    persistenceEnabled = false,
    private readonly memoryCacheFactory: () => TerrainProfileCache = () => new TerrainProfileCache(null),
  ) {
    this.persistenceEnabled = persistenceEnabled;
    this.cache = persistenceEnabled ? persistentCacheFactory() : memoryCacheFactory();
  }

  public get usesPersistentStorage(): boolean { return this.persistenceEnabled; }
  public get memoryEntryCount(): number { return this.cache.memoryEntryCount; }

  public get(key: string): Promise<ElevationProfile | null> { return this.cache.get(key); }
  public set(key: string, profile: ElevationProfile): Promise<boolean> { return this.cache.set(key, profile); }
  public delete(key: string): Promise<void> { return this.cache.delete(key); }
  public clear(): Promise<void> { return this.cache.clear(); }

  public setPersistenceEnabled(enabled: boolean): Promise<void> {
    return this.enqueueTransition(() => this.applyPersistenceState(enabled));
  }

  private async applyPersistenceState(enabled: boolean): Promise<void> {
    if (enabled) {
      if (this.persistenceEnabled) return;
      this.cache = this.persistentCacheFactory();
      this.persistenceEnabled = true;
      return;
    }

    if (this.persistenceEnabled) {
      const persistentCache = this.cache;
      this.cache = this.memoryCacheFactory();
      this.persistenceEnabled = false;
      await persistentCache.clearPersistentStorage();
      return;
    }

    // A prior deletion may have failed after the runtime already switched to memory.
    // Explicit retries must still reach IndexedDB, but ordinary necessary-only loads
    // never call this method and therefore never open the database.
    await this.persistentCacheFactory().clearPersistentStorage();
  }

  /** Clears stale IndexedDB data without enabling it for application reads. */
  public clearPersistentData(): Promise<void> {
    return this.enqueueTransition(async () => {
      if (this.persistenceEnabled) {
        await this.cache.clearPersistentStorage();
        return;
      }
      await this.persistentCacheFactory().clearPersistentStorage();
    });
  }

  private enqueueTransition(work: () => Promise<void>): Promise<void> {
    const operation = this.transitionTail.catch(() => undefined).then(work);
    this.transitionTail = operation.catch(() => undefined);
    return operation;
  }
}

export interface ConsentAwareTerrainProfileCacheOptions extends TerrainProfileCacheOptions {
  persistenceAllowed?: boolean;
}

export function createConsentAwareBrowserTerrainProfileCache(options: ConsentAwareTerrainProfileCacheOptions = {}): ConsentAwareTerrainProfileCache {
  const { persistenceAllowed = false, ...cacheOptions } = options;
  const persistentCacheFactory = (): TerrainProfileCache => {
    const store = typeof indexedDB === 'undefined' ? null : new IndexedDbTerrainProfileRecordStore(indexedDB);
    return new TerrainProfileCache(store, cacheOptions);
  };
  return new ConsentAwareTerrainProfileCache(persistentCacheFactory, persistenceAllowed, () => new TerrainProfileCache(null, cacheOptions));
}
